import type { ApiSchedule } from '@goalbuddy/client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge, Button, Card, Input, Label, PageHeader, Select } from '@/components/ui';
import { api } from '@/lib/api';
import { useMe, useSettings, useUsers } from '@/lib/hooks';
import { fmtDateTime } from '@/lib/utils';

function GeneralSettings() {
  const { data } = useSettings();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () =>
      api.updateSettings(
        Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, k === 'timezone' ? v : Number(v)])),
      ),
    onSuccess: () => (setDraft({}), toast.success('Settings saved')),
  });
  if (!data) return null;
  const val = (k: keyof typeof data) => draft[k] ?? String(data[k]);
  return (
    <Card className="space-y-3 p-4">
      <h2 className="font-semibold">General</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label>Focus tasks per day</Label>
          <Input
            type="number"
            min={1}
            max={20}
            value={val('focus_count')}
            onChange={(e) => setDraft({ ...draft, focus_count: e.target.value })}
          />
        </div>
        <div>
          <Label>Buddy focus limit</Label>
          <Input
            type="number"
            min={1}
            max={50}
            value={val('buddy_focus_limit')}
            onChange={(e) => setDraft({ ...draft, buddy_focus_limit: e.target.value })}
          />
        </div>
        <div>
          <Label>Timezone</Label>
          <Input value={val('timezone')} onChange={(e) => setDraft({ ...draft, timezone: e.target.value })} />
        </div>
        <div>
          <Label>Task is stale after (days)</Label>
          <Input
            type="number"
            min={1}
            value={val('stale_task_days')}
            onChange={(e) => setDraft({ ...draft, stale_task_days: e.target.value })}
          />
        </div>
      </div>
      <Button disabled={!Object.keys(draft).length} onClick={() => save.mutate()}>
        Save
      </Button>
    </Card>
  );
}

const DAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

function ScheduleRow({ s }: { s: ApiSchedule }) {
  const [time, setTime] = useState(s.time);
  const [rrule, setRrule] = useState(s.rrule);
  const update = useMutation({
    mutationFn: (patch: Parameters<typeof api.updateSchedule>[1]) => api.updateSchedule(s.id, patch),
    onSuccess: () => toast.success('Schedule saved'),
  });
  const weekly = /BYDAY=([A-Z]{2})/.exec(rrule)?.[1];
  return (
    <div className="flex flex-wrap items-end gap-3 border-b border-border py-3 last:border-0">
      <div className="w-36 font-medium">{s.type === 'daily_prep' ? 'Daily prep' : 'Weekly review'}</div>
      {s.type === 'weekly_review' && (
        <div>
          <Label>Day</Label>
          <Select value={weekly ?? 'FR'} onChange={(e) => setRrule(`FREQ=WEEKLY;BYDAY=${e.target.value}`)}>
            {DAYS.map((d) => (
              <option key={d}>{d}</option>
            ))}
          </Select>
        </div>
      )}
      <div>
        <Label>Time</Label>
        <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
      </div>
      <div className="flex-1">
        <Label>RRULE</Label>
        <Input value={rrule} onChange={(e) => setRrule(e.target.value)} />
      </div>
      <label className="flex h-8 items-center gap-1 text-sm">
        <input
          type="checkbox"
          checked={s.enabled}
          onChange={(e) => update.mutate({ enabled: e.target.checked })}
        />{' '}
        enabled
      </label>
      <Button
        size="sm"
        onClick={() => update.mutate({ time, rrule })}
        disabled={time === s.time && rrule === s.rrule}
      >
        Save
      </Button>
      {s.next_run_at && new Date(s.next_run_at) > new Date() && (
        <div className="w-full text-xs text-muted-fg">
          Next run moved to {fmtDateTime(s.next_run_at)}.{' '}
          <button className="text-primary underline" onClick={() => update.mutate({ next_run_at: null })}>
            Reset
          </button>
        </div>
      )}
    </div>
  );
}

function Schedules() {
  const { data } = useQuery({ queryKey: ['schedules'], queryFn: api.schedules });
  return (
    <Card className="p-4">
      <h2 className="font-semibold">Ritual schedules</h2>
      <p className="text-sm text-muted-fg">
        Times are in {data?.[0]?.timezone ?? 'Europe/Berlin'}. Move a single date in the calendar.
      </p>
      {data?.map((s) => (
        <ScheduleRow key={s.id + s.updated_at} s={s} />
      ))}
    </Card>
  );
}

function Tokens() {
  const { data: me } = useMe();
  const { data: users } = useUsers();
  const { data: tokens } = useQuery({ queryKey: ['tokens'], queryFn: api.tokens });
  const [user, setUser] = useState('claude-code-mac');
  const [label, setLabel] = useState('');
  const [created, setCreated] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => api.createToken({ user_name: user, label }),
    onSuccess: (r) => (setCreated(r.token), setLabel('')),
  });
  const revoke = useMutation({ mutationFn: (id: number) => api.revokeToken(id) });
  return (
    <Card className="space-y-3 p-4">
      <h2 className="font-semibold">API tokens</h2>
      <p className="text-sm text-muted-fg">
        Agents use tokens with the CLI (`gb login`). A token is shown only once.
      </p>
      {me?.via !== 'session' && (
        <p className="text-sm text-warn">Creating tokens needs a web login session.</p>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label>For user</Label>
          <Select value={user} onChange={(e) => setUser(e.target.value)}>
            {users
              ?.filter((u) => u.role !== 'system')
              .map((u) => (
                <option key={u.id}>{u.name}</option>
              ))}
          </Select>
        </div>
        <div className="flex-1">
          <Label>Label</Label>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. MacBook Claude Code"
          />
        </div>
        <Button disabled={!label.trim()} onClick={() => create.mutate()}>
          Create token
        </Button>
      </div>
      {created && (
        <div className="rounded-xl border border-ok/40 bg-ok/5 p-3 text-sm">
          <div className="mb-1 font-medium">Copy this token now. It will not be shown again.</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded bg-muted p-1.5 text-xs">{created}</code>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigator.clipboard.writeText(created).then(() => toast.success('Copied'))}
            >
              <Copy size={13} />
            </Button>
          </div>
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-fg">
          <tr>
            <th className="py-1">User</th>
            <th>Label</th>
            <th>Created</th>
            <th>Last used</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {tokens?.map((t) => (
            <tr key={t.id} className="border-t border-border">
              <td className="py-1.5">{t.user_name}</td>
              <td>{t.label}</td>
              <td>{fmtDateTime(t.created_at)}</td>
              <td>{t.last_used_at ? fmtDateTime(t.last_used_at) : '–'}</td>
              <td className="text-right">
                {t.revoked_at ? (
                  <Badge>revoked</Badge>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => confirm(`Revoke token "${t.label}"?`) && revoke.mutate(t.id)}
                  >
                    Revoke
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export function SettingsPage() {
  return (
    <div className="space-y-4">
      <PageHeader title="Settings" />
      <GeneralSettings />
      <Schedules />
      <Tokens />
    </div>
  );
}
